import React, { useEffect, useRef, useState } from "react";
import { stats as fallbackStats } from "../mock";
import { useContent, renderHighlightedTitle } from "../lib/useContent";

const Counter = ({ target, suffix }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const start = performance.now();
          const animate = (t) => {
            const p = Math.min((t - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(target * eased));
            if (p < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      });
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="font-display text-6xl md:text-7xl lg:text-8xl text-stone-950 tracking-tight">
      {value}
      <span className="text-[#12964A]">{suffix}</span>
    </span>
  );
};

const StatsSection = () => {
  const { field } = useContent("home");
  const pretitle = field("stats", "pretitle", "Numeri");
  const headline = field("stats", "headline", "Un progetto radicato nel territorio.");
  const highlightTerms = field("stats", "highlight_terms", ["radicato"]);
  const itemsRaw = field("stats", "items", null);
  const items = Array.isArray(itemsRaw) && itemsRaw.length
    ? itemsRaw.filter((s) => s.visible !== false)
    : fallbackStats;

  if (items.length === 0) return null;

  return (
    <section className="py-24 bg-stone-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, #6FC247 0%, transparent 40%), radial-gradient(circle at 80% 70%, #12964A 0%, transparent 40%)"
      }} />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          {pretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-stone-400 mb-4">{pretitle}</div>}
          <h2 className="font-display text-4xl md:text-6xl leading-tight [&_em]:text-[#6FC247]">
            {renderHighlightedTitle(headline, highlightTerms)}
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-6">
          {items.map((s, idx) => (
            <div key={`${s.label}-${idx}`} className="text-white">
              <div className="[&_span]:!text-white [&_span_span]:!text-[#6FC247]">
                <Counter target={Number(s.value) || 0} suffix={s.suffix || ""} />
              </div>
              <div className="mt-4 text-stone-400 text-sm md:text-base uppercase tracking-[0.18em]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
